import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import logoImg from "../img/logo.png";

/**
 * Genera un reporte PDF profesional con gráficos y tablas.
 * @param {Object} reportData - Datos del reporte
 * @param {Object} reportData.filtros - Filtros aplicados (Compania, Planta, Fechas...)
 * @param {Array} reportData.chartIds - IDs de los contenedores DOM de los gráficos a capturar
 * @param {Array} reportData.tableDataRepetibilidad - Datos para tabla Repetibilidad
 * @param {Array} reportData.tableDataGeneral - Datos para tabla General
 */
export const generateCalibrationPDF = async ({
    filtros,
    chartIds = [],
    tableDataRepetibilidad = [],
    tableDataGeneral = [],
    empresa = {} // New param
}) => {
    const doc = new jsPDF("p", "mm", "a4");
    const margin = 15;
    let currentY = margin;
    const dateStr = new Date().toLocaleDateString("es-ES", { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    // --- 1. HEADER & COVER INFO ---

    // Logo
    try {
        const img = new Image();
        img.src = logoImg;
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
        });
        // Maintain aspect ratio, width 40mm
        const imgWidth = 40;
        const imgHeight = (img.height * imgWidth) / img.width;
        doc.addImage(img, "JPEG", margin, currentY, imgWidth, imgHeight);

        // Title (Right of logo)
        doc.setFontSize(20); // Slightly smaller to fit lines
        doc.setTextColor(40, 53, 147);
        doc.text("Reporte Ejecutivo de Calibración", margin + imgWidth + 10, currentY + 8);

        // Subtitle / Date
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generado el: ${dateStr}`, margin + imgWidth + 10, currentY + 14);

        // --- EMPRESA INFO ---
        if (empresa && empresa.NombreLegal) {
            doc.setFontSize(9);
            doc.setTextColor(50);
            let empY = currentY + 20;
            doc.text(empresa.NombreLegal, margin + imgWidth + 10, empY);

            if (empresa.DireccionPrincipal) {
                empY += 4;
                doc.setFontSize(8);
                doc.text(`${empresa.DireccionPrincipal} - ${empresa.Ciudad || ''}`, margin + imgWidth + 10, empY);
            }
            if (empresa.ID_Fiscal_Universal) {
                empY += 4;
                doc.text(`ID Fiscal: ${empresa.ID_Fiscal_Universal}`, margin + imgWidth + 10, empY);
            }
            if (empresa.TelefonoOficina || empresa.EmailContacto) {
                empY += 4;
                doc.text(`Contacto: ${empresa.TelefonoOficina || ''} | ${empresa.EmailContacto || ''}`, margin + imgWidth + 10, empY);
            }
        }

        currentY += Math.max(imgHeight, 35) + 10;

        // Separator Line
        doc.setDrawColor(40, 53, 147);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY, doc.internal.pageSize.getWidth() - margin, currentY);
        currentY += 10;
    } catch (e) {
        console.warn("Logo load failed", e);
        // Fallback
        doc.setFontSize(22);
        doc.text("Reporte Ejecutivo de Calibración", margin, currentY + 10);
        currentY += 20;
    }


    // --- 2. CONTEXTO (Formato Ejecutivo) ---
    doc.setFontSize(14);
    doc.setTextColor(40, 53, 147);
    doc.text("1. Contexto de Auditoría y Alcance", margin, currentY);
    currentY += 10;

    // Tabla de Parámetros (Reemplaza "Filtros")
    const paramsBody = [
        ['Rango de Análisis', `${filtros.fechaInicio || 'N/A'} al ${filtros.fechaFin || 'N/A'}`],
        ['Compañía Auditada', filtros.compania || 'Consolidado General'],
        ['Planta Operativa', filtros.planta || 'Todas las Plantas'],
        ['Mesa de Carga', filtros.mesacarga || 'Todas'],
        ['Brazo de Carga', filtros.brazo || 'Todos'],
        ['Producto', filtros.producto || 'Multiproducto'],
        ['Tipo de Flujo', filtros.tipoFlujo || 'Todos']
    ];

    autoTable(doc, {
        startY: currentY,
        body: paramsBody,
        theme: 'grid',
        bodyStyles: { fontSize: 10, cellPadding: 4, textColor: 60 },
        columnStyles: {
            0: { fontStyle: 'bold', fillColor: [240, 242, 245], cellWidth: 50 },
            1: { cellWidth: 'auto' }
        },
        margin: { left: margin, right: margin }
    });
    currentY = doc.lastAutoTable.finalY + 15;

    // Resumen Ejecutivo (Filler Professional Text)
    doc.setFontSize(11);
    doc.setTextColor(40);
    doc.setFont(undefined, 'bold');
    doc.text("Resumen Ejecutivo Técnica", margin, currentY);
    currentY += 6;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);

    const pageWidth = doc.internal.pageSize.getWidth();
    const executiveSummary = `El presente informe técnico consolida los resultados de la calibración volumétrica y control metrológico efectuado durante el periodo declarado. El objetivo principal es validar el desempeño de los instrumentos de medición de transferencia de custodia de acuerdo con los estándares internacionales API MPMS (Manual of Petroleum Measurement Standards).

La auditoría abarca la verificación de la repetibilidad (KPI L1), la distribución estadística de los errores volumétricos y la linealidad del factor del medidor (K-Factor) a través de distintos regímenes de caudal. Se han procesado los datos crudos de las computadoras de flujo para identificar posibles desviaciones sistemáticas (bias) o aleatorias que pudiesen afectar el balance de masas de la planta.

El análisis que se presenta a continuación desglosa el comportamiento metrológico en cuatro dimensiones críticas: consistencia operativa, sesgo de medición, correlación de error por volumen despachado y trazabilidad individual por carga. Este enfoque integral asegura la detección temprana de derivas instrumentales y garantiza la transparencia en las transacciones comerciales.`;

    const splitSummary = doc.splitTextToSize(executiveSummary, pageWidth - (margin * 2));
    doc.text(splitSummary, margin, currentY);

    currentY += (splitSummary.length * 5) + 20;


    // --- 3. GRÁFICOS (1 por Página con Tabla de Datos e Interpretación) ---

    // Extract extra data logic (same as before) logic reused via scope/derivation

    const chartMetadata = {
        'chart-repetibilidad': {
            title: "Análisis de Repetibilidad (KPI L1)",
            formula: "R = |Max(Vi) - Min(Vi)| ≤ 0.05%",
            desc: "Evaluación de la consistencia del instrumento bajo condiciones idénticas de operación. El gráfico visualiza la dispersión de las lecturas consecutivas para validar que el sistema mantiene su precisión dentro de los rangos de tolerancia establecidos por la normativa API MPMS.",
            engNote: "Ingeniería: La estabilidad del KPI L1 es el primer indicador de salud mecánica. Una dispersión baja y constante (banda estrecha) valida la integridad de los álabes/rodamientos del medidor. Picos esporádicos suelen correlacionarse con presencia de aire o inestabilidad en la presión de bombeo."
        },
        'chart-distribucion': {
            title: "Distribución de Errores",
            formula: "E(%) = ((V_ind - V_ref) / V_ref) × 100",
            desc: "Histograma de variabilidad de errores. Muestra la frecuencia de ocurrencia de cada magnitud de error, permitiendo visualizar la Tendencia Central (Sesgo) y la Dispersión (Precisión).",
            engNote: "Ingeniería: Una distribución Gaussiana (Normal) centrada en cero es el estado ideal. Desplazamientos del centro de masa indican un error sistemático corregible mediante calibración del K-Factor. Una distribución 'achatada' (Platicúrtica) sugiere alta variabilidad en las condiciones de proceso."
        },
        'chart-error-volumen': {
            title: "Promedio de Error por Volumen",
            formula: "Avg = Σ(E) / n",
            desc: "Tendencia del error promedio en función del volumen total entregado. Permite evaluar la linealidad del instrumento.",
            engNote: "Ingeniería: Los medidores de desplazamiento positivo pueden presentar 'slippage' (resbalamiento) a bajos caudales/volúmenes. Este gráfico permite diagnosticar si el medidor sub-contabiliza en entregas pequeñas, lo cual es crítico para la facturación de cargas parciales."
        },
        'chart-carga': {
            title: "Trazabilidad por Carga",
            formula: "E_neto = Error Relativo",
            desc: "Detalle punto a punto por carga cronológica.",
            engNote: "Ingeniería: Este nivel de detalle permite la auditoría forense de cada transacción. Es útil para correlacionar errores altos con eventos operativos específicos (ej. cambios de turno, mantenimiento, temperaturas extremas) que no son visibles en los promedios globales."
        }
    };

    for (const chartId of chartIds) {
        const element = document.getElementById(chartId);
        if (element) {
            try {
                doc.addPage();
                currentY = margin;

                // 1. Header
                const meta = chartMetadata[chartId] || {};
                doc.setFontSize(14);
                doc.setTextColor(40, 53, 147);
                doc.text(`2.${chartIds.indexOf(chartId) + 1} ${meta.title || 'Gráfico'}`, margin, currentY);
                currentY += 10;

                // 2. High Quality Image (MAXIMIZED)
                // Scale 4 for Ultra HD
                const canvas = await html2canvas(element, { scale: 4, useCORS: true, logging: false });
                const imgData = canvas.toDataURL("image/png");
                const imgProps = doc.getImageProperties(imgData);
                // const pageWidth = doc.internal.pageSize.getWidth(); // Already defined above
                const maxImgWidth = pageWidth - (margin * 2);

                // Max height 150mm (Huge)
                let imgHeight = (imgProps.height * maxImgWidth) / imgProps.width;
                const finalHeight = Math.min(imgHeight, 150);
                const finalWidth = (imgProps.width * finalHeight) / imgProps.height;
                const xOffset = margin + (maxImgWidth - finalWidth) / 2;

                doc.addImage(imgData, "PNG", xOffset, currentY, finalWidth, finalHeight);
                currentY += finalHeight + 8;

                // 3. Formula
                doc.setFontSize(10);
                doc.setTextColor(60);
                doc.text(`Fórmula: ${meta.formula || '-'}`, margin, currentY);
                currentY += 6;
                const splitDesc = doc.splitTextToSize(meta.desc || "", maxImgWidth);
                doc.text(splitDesc, margin, currentY);
                currentY += (splitDesc.length * 4) + 8;

                // 4. Data Table (Tablita Profesional) - Compact rows to save space for Note
                let tableHead = [];
                let tableBody = [];

                if (chartId === 'chart-repetibilidad') {
                    tableHead = [['Mesa', 'Brazo', 'Fecha', 'KPI L1', 'Estado']];
                    tableBody = tableDataRepetibilidad.slice(0, 12).map(r => [
                        r.MesaCarga, r.Brazo, new Date(r.Fecha).toLocaleDateString(),
                        Number(r.KpiL1).toFixed(4), r.EstadoSecuenciaL1 || '-'
                    ]);
                } else if (chartId === 'chart-distribucion') {
                    tableHead = [['Producto', 'Carga', 'Err Bruto', 'Err Neto']];
                    tableBody = tableDataGeneral.slice(0, 12).map(r => [
                        r.Producto, r.carga, Number(r.ErrorVolumenBruto).toFixed(4), Number(r.ErrorVolumenNeto).toFixed(4)
                    ]);
                } else if (chartId === 'chart-error-volumen') {
                    const groups = {};
                    tableDataGeneral.forEach(r => {
                        const d = new Date(r.Fecha).toLocaleDateString();
                        if (!groups[d]) groups[d] = { sum: 0, count: 0 };
                        groups[d].sum += parseFloat(r.ErrorVolumenNeto || 0);
                        groups[d].count++;
                    });
                    tableHead = [['Fecha', 'Promedio Error Neto', 'N° Cargas']];
                    tableBody = Object.entries(groups).slice(0, 12).map(([date, val]) => [
                        date, (val.sum / val.count).toFixed(4), val.count
                    ]);
                } else if (chartId === 'chart-carga') {
                    tableHead = [['Fecha', 'Carga', 'Err Bruto', 'Err Neto']];
                    tableBody = tableDataGeneral.slice(0, 12).map(r => [
                        new Date(r.Fecha).toLocaleDateString(), r.carga, r.ErrorVolumenBruto, r.ErrorVolumenNeto
                    ]);
                }

                if (tableBody.length > 0) {
                    autoTable(doc, {
                        startY: currentY,
                        head: tableHead,
                        body: tableBody,
                        theme: 'striped',
                        headStyles: { fillColor: [45, 55, 72], fontSize: 9, halign: 'center' }, // Dark Slate
                        bodyStyles: { fontSize: 8, cellPadding: 2, textColor: 50 },
                        columnStyles: { 0: { halign: 'center' }, 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' } },
                        margin: { left: margin, right: margin }
                    });
                    currentY = doc.lastAutoTable.finalY + 10;
                }

                // 5. Engineering Interpretation (Footer)
                // Ensure it fits
                if (currentY + 30 < doc.internal.pageSize.getHeight() - margin) {
                    doc.setDrawColor(200);
                    doc.setFillColor(250, 250, 250);
                    doc.roundedRect(margin, currentY, maxImgWidth, 25, 2, 2, 'FD');

                    doc.setFontSize(9);
                    doc.setTextColor(30, 64, 175); // Blue-800
                    doc.setFont(undefined, 'bold');
                    doc.text("INTERPRETACIÓN DE INGENIERÍA:", margin + 4, currentY + 6);

                    doc.setFont(undefined, 'normal');
                    doc.setTextColor(60);
                    const engNote = meta.engNote || "Análisis conforme a estándares.";
                    const splitEng = doc.splitTextToSize(engNote, maxImgWidth - 8);
                    doc.text(splitEng, margin + 4, currentY + 12);
                }

            } catch (err) {
                console.error(`Error processing ${chartId}:`, err);
            }
        }
    }

    currentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : 250;


    // --- 4. DATA TABLES ---

    // Tabla Repetibilidad
    if (tableDataRepetibilidad.length > 0) {
        doc.addPage();
        currentY = margin;
        doc.setFontSize(14);
        doc.setTextColor(40, 53, 147);

        doc.text("3. Detalle de Repetibilidad (KPI L1)", margin, currentY);
        currentY += 8;

        autoTable(doc, {
            startY: currentY,
            head: [['Fecha', 'Cía.', 'Planta', 'Mesa', 'Brazo', 'Prod.', 'KPI L1', 'Estado', 'Secuencia']],
            body: tableDataRepetibilidad.map(row => [
                new Date(row.Fecha).toLocaleDateString(),
                row.Compania,
                row.Planta,
                row.MesaCarga,
                row.Brazo,
                row.Producto,
                row.KpiL1 != null ? Number(row.KpiL1).toFixed(4) : '-',
                row.EstadoSecuenciaL1,
                row.ValoresSecuencia3Consecutivos_L1
            ]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [46, 204, 113] }, // Emerald/Greenish for Validation
        });
        currentY = doc.lastAutoTable.finalY + 15;
    }

    // Tabla General (Si hay espacio o nueva pagina)
    if (tableDataGeneral.length > 0) {
        // Check space, roughly
        if (currentY > 250) {
            doc.addPage();
            currentY = margin;
        }

        doc.setFontSize(12);
        doc.text("4. Resultados Generales de Calibración", margin, currentY);
        currentY += 6;

        autoTable(doc, {
            startY: currentY,
            head: [['Fecha', 'Brazo', 'Carga', 'Err. Vol. Bruto', 'Err. Vol. Neto', 'Dif. Bruto', 'Dif. Neto']],
            body: tableDataGeneral.slice(0, 500).map(row => [ // Limit rows for performance/size?
                new Date(row.Fecha).toLocaleDateString(),
                row.Brazo,
                row.carga,
                row.ErrorVolumenBruto,
                row.ErrorVolumenNeto,
                row.DifVolBruto,
                row.DifVolNeto
            ]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [52, 73, 94] },
        });
    }

    // Footer Numbering
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Página ${i} de ${pageCount} - GireSis Web Control System`, 105, 290, { align: "center" });
    }

    doc.save(`Reporte_Calibracion_${filtros.planta || 'Global'}_${dateStr}.pdf`);
};
